import React, { useContext, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Camera,
  User,
  Mail,
  Phone,
  MapPin,
  Bell,
  Shield,
  Loader2,
  CheckCircle,
  MessageSquare,
  Smartphone,
  AtSign,
} from "lucide-react";
import useModal from "hooks/use-modal";
import API from "../../utils/api";
import { MainStore } from "../../stores/Main";
import Select from "react-select";
import SwitchToggle from "../../components/switch-toggle/switch-toggle";
import history from "../../utils/history";
import MaskedInput from "react-text-mask";
import { trackProfileUpdated, trackOptinPreferenceChanged, trackProfilePictureUploaded } from "../../utils/posthogTracking";

const emailTest =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const states = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "American Samoa", value: "AS" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "District Of Columbia", value: "DC" },
  { label: "Federated States Of Micronesia", value: "FM" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Guam", value: "GU" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Marshall Islands", value: "MH" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Northern Mariana Islands", value: "MP" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Palau", value: "PW" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Puerto Rico", value: "PR" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virgin Islands", value: "VI" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
];

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "rgba(17, 24, 39, 0.5)",
    borderColor: state.isFocused
      ? "rgba(59, 130, 246, 0.5)"
      : "rgba(75, 85, 99, 0.5)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
    "&:hover": {
      borderColor: "rgba(59, 130, 246, 0.5)",
    },
    minHeight: "48px",
    borderRadius: "0.75rem",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "rgba(17, 24, 39, 0.95)",
    backdropFilter: "blur(12px)",
    borderRadius: "0.75rem",
    border: "1px solid rgba(75, 85, 99, 0.5)",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "rgba(59, 130, 246, 0.3)"
      : state.isFocused
        ? "rgba(59, 130, 246, 0.1)"
        : "transparent",
    color: state.isSelected ? "#fff" : "rgba(209, 213, 219, 1)",
    "&:hover": {
      backgroundColor: "rgba(59, 130, 246, 0.2)",
      color: "#fff",
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#fff",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "rgba(156, 163, 175, 1)",
  }),
  input: (provided) => ({
    ...provided,
    color: "#fff",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "rgba(156, 163, 175, 1)",
    "&:hover": {
      color: "#fff",
    },
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    backgroundColor: "rgba(75, 85, 99, 0.5)",
  }),
};

const FormGeneralInfo = ({ shortVersion = false }) => {
  const [uploadError, setUploadError] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { openModal, modalTypes, closeModal } = useModal();
  const mainStore = useContext(MainStore);
  const [profile, setProfile] = useState(mainStore.profile);
  const [originalProfile, setOriginalProfile] = useState(mainStore.profile);

  const itemAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  const openEsign = async () => {
    try {
      const response = await API.get("/disclosures?mode=esign", null);
      const content = response.data.response[0]?.Data?.EsignConsent?.text;
      const acceptTerms = () => {
        setProfile((cur) => ({
          ...cur,
          agency: {
            ...cur.agency,
            optins: {
              ...cur.agency.optins,
              allow_esign: true,
            },
          },
        }));
        closeModal();
      };
      const rejectTerms = () => {
        setProfile((cur) => ({
          ...cur,
          agency: {
            ...cur.agency,
            optins: {
              ...cur.agency.optins,
              allow_esign: false,
            },
          },
        }));
        closeModal();
      };
      openModal(modalTypes.ModalTermsScroller, {
        title: "E-Sign Consent",
        content,
        accept: acceptTerms,
        reject: rejectTerms,
      });
    } catch (e) {
      console.log(e);
    }
  };

  const profilePicUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setUploadError("File size cannot exceed 5MB");
        return;
      }

      if (/^image\//i.test(file.type)) {
        let formData = new FormData();
        formData.append("file", file);
        try {
          setUploading(true);
          const profilePic = await API.put("/profile/pic", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          mainStore.setAvatar(profilePic.data.Img);
          setUploadError("");
          // Track profile picture upload
          trackProfilePictureUploaded();
        } catch (e) {
          setUploadError("Failed to upload image");
        }
        setUploading(false);
      } else {
        setUploadError("Please select an image file");
      }
    }
  };

  const verifyAddress = async () => {
    try {
      await API.post("/address/verify");
      mainStore.handleVerifyAddress();
    } catch (e) {}
  };

  const saveProfile = async (check, logout) => {
    if (!emailTest.test(profile.email)) {
      setError("Please enter a valid email address");
      return;
    }

    const updateProfile = { ...profile };
    if (mainStore.agency) {
      updateProfile.agency_optins = {
        allow_phone: updateProfile.agency.optins.allow_phone,
        allow_push: updateProfile.agency.optins.allow_push,
        allow_sms: updateProfile.agency.optins.allow_sms,
        allow_esign: updateProfile.agency.optins.allow_esign,
        allow_email: updateProfile.agency.optins.allow_email,
      };
    }
    delete updateProfile.agency;

    if (
      check &&
      mainStore.profile.agency.optins.allow_esign &&
      !updateProfile.agency_optins.allow_esign
    ) {
      openModal(modalTypes.ModalEmailOptOut, {
        title: "Email Opt-out",
        accept: () => {
          closeModal();
          saveProfile(false, true);
        },
        reject: () => {
          closeModal();
        },
      });
      return;
    }

    try {
      setSaving(true);
      setError("");

      const response = await API.put("/profile", updateProfile);

      if (response.data.result_code) {
        setError(response.data.message || "Unable to save changes");
      } else {
        // Track profile update - determine which fields changed
        const fieldsChanged = [];
        if (originalProfile.first_name !== profile.first_name) fieldsChanged.push("first_name");
        if (originalProfile.last_name !== profile.last_name) fieldsChanged.push("last_name");
        if (originalProfile.email !== profile.email) fieldsChanged.push("email");
        if (originalProfile.phone !== profile.phone) fieldsChanged.push("phone");
        if (originalProfile.address !== profile.address) fieldsChanged.push("address");
        if (originalProfile.city !== profile.city) fieldsChanged.push("city");
        if (originalProfile.state !== profile.state) fieldsChanged.push("state");
        if (originalProfile.zip !== profile.zip) fieldsChanged.push("zip");
        
        if (fieldsChanged.length > 0) {
          trackProfileUpdated(fieldsChanged);
        }
        
        // Track opt-in preference changes
        if (mainStore.agency) {
          if (originalProfile.agency?.optins?.allow_email !== profile.agency?.optins?.allow_email) {
            trackOptinPreferenceChanged("email", profile.agency.optins.allow_email);
          }
          if (originalProfile.agency?.optins?.allow_sms !== profile.agency?.optins?.allow_sms) {
            trackOptinPreferenceChanged("sms", profile.agency.optins.allow_sms);
          }
        }
        
        // Update original profile reference after successful save
        setOriginalProfile(profile);
        
        if (
          mainStore.agency &&
          (mainStore.profile.agency.optins.allow_esign === false ||
            mainStore.profile.agency.optins.allow_esign === undefined) &&
          updateProfile.agency_optins.allow_esign
        ) {
          await API.post("/disclosures/accept?mode=esign");
        }
        if (!mainStore.verifiedAddress && shortVersion) {
          await verifyAddress();
        } else {
          if (logout) {
            mainStore.handleLogout();
          } else {
            const newProfile = await API.get("/profile");
            mainStore.setProfile(newProfile.data);
          }
        }
      }
    } catch (e) {
      setError("An error occurred while saving your profile");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      {/* Profile Picture Card */}
      {!shortVersion && (
        <motion.div
          variants={itemAnimation}
          initial="hidden"
          animate="visible"
          className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-3xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20">
              <Camera className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                Profile Picture
              </h3>
              <p className="text-sm text-gray-400">Upload your profile photo</p>
            </div>
          </div>

          <div className="flex flex-col items-center">
            <input
              type="file"
              hidden
              id="profileupload"
              accept="image/*,.jpg,.png"
              onChange={profilePicUpload}
            />
            <div
              className="relative group cursor-pointer"
              onClick={() => document.getElementById("profileupload").click()}
            >
              <img
                className="w-32 h-32 rounded-full object-cover border-4 border-white/10 group-hover:border-blue-500/50 transition-all duration-300"
                src={`data:image/png;base64,${mainStore.avatar}`}
                alt="Profile"
              />
              {uploading ? (
                <div className="absolute inset-0 rounded-full bg-black/70 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                </div>
              ) : (
                <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <Camera className="w-8 h-8 text-white" />
                </div>
              )}
            </div>

            {uploadError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-lg"
              >
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-sm text-red-400">{uploadError}</span>
              </motion.div>
            )}

            <p className="mt-3 text-sm text-gray-400">
              Click to upload • Max size 5MB • JPG, PNG
            </p>
          </div>
        </motion.div>
      )}

      {/* Personal Information Card */}
      <motion.div
        variants={itemAnimation}
        initial="hidden"
        animate="visible"
        transition={{ delay: 0.1 }}
        className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-3xl p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20">
            <User className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">
              Personal Information
            </h3>
            <p className="text-sm text-gray-400">Your basic profile details</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              First Name
            </label>
            <input
              type="text"
              className={`w-full px-4 py-3 rounded-xl transition-all ${
                saving || mainStore.agency
                  ? "bg-gray-900/20 border border-gray-800 text-gray-500 cursor-not-allowed"
                  : "bg-gray-900/50 border border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              }`}
              placeholder="Enter first name"
              disabled={saving || mainStore.agency}
              defaultValue={mainStore.profile.first_name}
              onChange={(e) =>
                setProfile({ ...profile, first_name: e.target.value })
              }
            />
            {mainStore.agency && (
              <p className="mt-1 text-xs text-gray-500">
                Contact {mainStore.agencyDetails.name} to update
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Last Name
            </label>
            <input
              type="text"
              className={`w-full px-4 py-3 rounded-xl transition-all ${
                saving || mainStore.agency
                  ? "bg-gray-900/20 border border-gray-800 text-gray-500 cursor-not-allowed"
                  : "bg-gray-900/50 border border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              }`}
              placeholder="Enter last name"
              disabled={saving || mainStore.agency}
              defaultValue={mainStore.profile.last_name}
              onChange={(e) =>
                setProfile({ ...profile, last_name: e.target.value })
              }
            />
            {mainStore.agency && (
              <p className="mt-1 text-xs text-gray-500">
                Contact {mainStore.agencyDetails.name} to update
              </p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Email Address
            </label>
            <div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  className={`w-full pl-11 pr-4 py-3 rounded-xl transition-all ${
                    saving || mainStore.agency
                      ? "bg-gray-900/20 border border-gray-800 text-gray-500 cursor-not-allowed"
                      : "bg-gray-900/50 border border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  }`}
                  placeholder="Enter email address"
                  disabled={saving || mainStore.agency}
                  defaultValue={mainStore.profile.email}
                  onChange={(e) =>
                    setProfile({ ...profile, email: e.target.value })
                  }
                />
              </div>
              {mainStore.agency && (
                <p className="mt-1 text-xs text-gray-500 ml-11">
                  Visit your Freede profile to update
                </p>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Contact Information Card */}
      <motion.div
        variants={itemAnimation}
        initial="hidden"
        animate="visible"
        transition={{ delay: 0.2 }}
        className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-3xl p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/20">
            <MapPin className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">
              Contact Information
            </h3>
            <p className="text-sm text-gray-400">
              Your location and contact details
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Phone Number
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <MaskedInput
                  className={`w-full pl-11 pr-4 py-3 rounded-xl transition-all ${
                    saving
                      ? "bg-gray-900/20 border border-gray-800 text-gray-500 cursor-not-allowed"
                      : "bg-gray-900/50 border border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  }`}
                  mask={[
                    "(",
                    /\d/,
                    /\d/,
                    /\d/,
                    ")",
                    " ",
                    /\d/,
                    /\d/,
                    /\d/,
                    "-",
                    /\d/,
                    /\d/,
                    /\d/,
                    /\d/,
                  ]}
                  placeholder="(555) 123-4567"
                  disabled={saving}
                  defaultValue={mainStore.profile.phone}
                  onChange={(e) =>
                    setProfile({ ...profile, phone: e.target.value })
                  }
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                State
              </label>
              <Select
                options={states}
                styles={customSelectStyles}
                placeholder="Select state"
                isDisabled={saving}
                defaultValue={states.filter(
                  ({ value }) => value === mainStore.profile.state
                )}
                onChange={(selected) =>
                  setProfile({ ...profile, state: selected.value })
                }
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              City
            </label>
            <input
              type="text"
              className={`w-full px-4 py-3 rounded-xl transition-all ${
                saving
                  ? "bg-gray-900/20 border border-gray-800 text-gray-500 cursor-not-allowed"
                  : "bg-gray-900/50 border border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              }`}
              placeholder="Enter city"
              disabled={saving}
              defaultValue={mainStore.profile.city}
              onChange={(e) => setProfile({ ...profile, city: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Street Address
            </label>
            <input
              type="text"
              className={`w-full px-4 py-3 rounded-xl transition-all ${
                saving
                  ? "bg-gray-900/20 border border-gray-800 text-gray-500 cursor-not-allowed"
                  : "bg-gray-900/50 border border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              }`}
              placeholder="Enter street address"
              disabled={saving}
              defaultValue={mainStore.profile.address}
              onChange={(e) =>
                setProfile({ ...profile, address: e.target.value })
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Address Line 2
            </label>
            <input
              type="text"
              className={`w-full px-4 py-3 rounded-xl transition-all ${
                saving
                  ? "bg-gray-900/20 border border-gray-800 text-gray-500 cursor-not-allowed"
                  : "bg-gray-900/50 border border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              }`}
              placeholder="Apartment, suite, etc. (optional)"
              disabled={saving}
              defaultValue={mainStore.profile.address2}
              onChange={(e) =>
                setProfile({ ...profile, address2: e.target.value })
              }
            />
          </div>

          <div className="w-full md:w-1/3">
            <label className="block text-sm font-medium text-gray-400 mb-2">
              ZIP Code
            </label>
            <input
              type="text"
              className={`w-full px-4 py-3 rounded-xl transition-all ${
                saving
                  ? "bg-gray-900/20 border border-gray-800 text-gray-500 cursor-not-allowed"
                  : "bg-gray-900/50 border border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              }`}
              placeholder="12345"
              disabled={saving}
              defaultValue={mainStore.profile.postal_code}
              onChange={(e) =>
                setProfile({ ...profile, postal_code: e.target.value })
              }
            />
          </div>
        </div>
      </motion.div>

      {/* Communication Preferences */}
      {!shortVersion && (
        <>
          {/* Freede Communications Card */}
          <motion.div
            variants={itemAnimation}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.3 }}
            className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-3xl p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20">
                <Bell className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Freede Communications
                </h3>
                <p className="text-sm text-gray-400">
                  Control what marketing and non-transactional communications
                  you receive
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-900/30 rounded-xl">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-5 h-5 text-gray-400" />
                  <div>
                    <span className="text-white font-medium">
                      SMS/Text Messages
                    </span>
                    <p className="text-sm text-gray-400">
                      Receive updates via text
                    </p>
                  </div>
                </div>
                <SwitchToggle
                  active={profile.allow_sms}
                  onClick={() =>
                    setProfile({ ...profile, allow_sms: !profile.allow_sms })
                  }
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-900/30 rounded-xl">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <span className="text-white font-medium">Email</span>
                    <p className="text-sm text-gray-400">
                      Receive updates via email
                    </p>
                  </div>
                </div>
                <SwitchToggle
                  active={profile.allow_email}
                  onClick={() =>
                    setProfile({
                      ...profile,
                      allow_email: !profile.allow_email,
                    })
                  }
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-900/30 rounded-xl">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <div>
                    <span className="text-white font-medium">Phone Calls</span>
                    <p className="text-sm text-gray-400">
                      Receive updates via phone
                    </p>
                  </div>
                </div>
                <SwitchToggle
                  active={profile.allow_phone}
                  onClick={() =>
                    setProfile({
                      ...profile,
                      allow_phone: !profile.allow_phone,
                    })
                  }
                />
              </div>
            </div>
          </motion.div>

          {/* Agency Communications Card */}
          {mainStore.agency && (
            <motion.div
              variants={itemAnimation}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.4 }}
              className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-3xl p-6"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-orange-500/20 to-red-500/20">
                  <Shield className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {profile.agency.name} Communications
                  </h3>
                  <p className="text-sm text-gray-400">
                    Control communications from your agency
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-900/30 rounded-xl">
                  <div className="flex items-center gap-3 flex-1">
                    <AtSign className="w-5 h-5 text-gray-400" />
                    <div className="flex-1">
                      <span className="text-white font-medium">
                        Email Communications
                      </span>
                      <p className="text-sm text-gray-400">
                        I agree to receive future correspondence from{" "}
                        {mainStore.agencyDetails.name} by email, including any
                        required disclosures, in accordance with this{" "}
                        <button
                          onClick={openEsign}
                          className="text-blue-400 hover:text-blue-300 underline"
                        >
                          notice
                        </button>
                      </p>
                    </div>
                  </div>
                  <SwitchToggle
                    active={profile.agency.optins.allow_esign}
                    onClick={() => openEsign()}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-900/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-gray-400" />
                    <div>
                      <span className="text-white font-medium">
                        SMS/Text Messages
                      </span>
                      <p className="text-sm text-gray-400">
                        Receive agency updates via text
                      </p>
                    </div>
                  </div>
                  <SwitchToggle
                    active={profile.agency.optins.allow_sms}
                    onClick={() => {
                      setProfile({
                        ...profile,
                        agency: {
                          ...profile.agency,
                          optins: {
                            ...profile.agency.optins,
                            allow_sms: !profile.agency.optins.allow_sms,
                          },
                        },
                      });
                    }}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-900/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <div>
                      <span className="text-white font-medium">
                        Phone Calls
                      </span>
                      <p className="text-sm text-gray-400">
                        Receive agency updates via phone
                      </p>
                    </div>
                  </div>
                  <SwitchToggle
                    active={profile.agency.optins.allow_phone}
                    onClick={() =>
                      setProfile({
                        ...profile,
                        agency: {
                          ...profile.agency,
                          optins: {
                            ...profile.agency.optins,
                            allow_phone: !profile.agency.optins.allow_phone,
                          },
                        },
                      })
                    }
                  />
                </div>
              </div>
            </motion.div>
          )}
        </>
      )}

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-xl"
        >
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <span className="text-red-400">{error}</span>
        </motion.div>
      )}

      {/* Action Buttons */}
      <motion.div
        variants={itemAnimation}
        initial="hidden"
        animate="visible"
        transition={{ delay: 0.5 }}
        className="flex flex-col sm:flex-row gap-3 justify-end pt-4"
      >
        {!shortVersion ? (
          <>
            <button
              onClick={() => history.push("/dashboard/profile")}
              disabled={saving}
              className="px-6 py-3 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white rounded-xl transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={() => saveProfile(true, false)}
              disabled={saving}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 disabled:from-gray-600 disabled:to-gray-700 text-white rounded-xl transition-all font-medium shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => verifyAddress()}
              disabled={saving}
              className="px-6 py-3 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white rounded-xl transition-colors font-medium"
            >
              Verify
            </button>
            <button
              onClick={() => saveProfile(false, false)}
              disabled={saving}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 disabled:from-gray-600 disabled:to-gray-700 text-white rounded-xl transition-all font-medium shadow-lg hover:shadow-xl"
            >
              {saving ? "Saving..." : "Save and Verify Changes"}
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default FormGeneralInfo;
